import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { Minus, Plus, ArrowLeft, Check } from "lucide-react";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const { add } = useCart();
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => setProduct(r.data)).catch(() => nav("/shop"));
  }, [slug, nav]);

  if (!product) return <div className="pt-32 max-w-7xl mx-auto px-6">Loading…</div>;

  const onAdd = () => {
    add(product, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  return (
    <div data-testid="product-detail-page" className="pt-28 pb-24 max-w-7xl mx-auto px-6 md:px-12">
      <Link data-testid="back-to-shop" to="/shop" className="inline-flex items-center gap-2 text-xs uppercase tracking-overline text-[#7A7A7A] mb-10 hover:text-[#C84B31]">
        <ArrowLeft size={14}/> Back to Cellar
      </Link>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-7">
          <div className="aspect-[4/5] bg-[#EAE6DB] overflow-hidden">
            <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
          </div>
        </div>

        <div className="md:col-span-5">
          <p className="text-xs uppercase tracking-overline text-[#4A5D23] mb-4">{product.category} · {product.origin}</p>
          <h1 className="font-serif-display text-5xl md:text-6xl tracking-tighter leading-[1.02]">{product.name}</h1>

          <div className="mt-6 flex items-baseline gap-3">
            <span className="font-serif-display text-4xl text-[#C84B31]">${product.price_usd.toFixed(2)}</span>
            <span className="text-xs uppercase tracking-overline text-[#7A7A7A]">per {product.unit_label}</span>
          </div>

          <p className="mt-8 text-[#4A4A4A] leading-relaxed text-lg">{product.description}</p>

          {product.tasting_notes?.length > 0 && (
            <div className="mt-8">
              <p className="text-xs uppercase tracking-overline text-[#7A7A7A] mb-3">Tasting Notes</p>
              <div className="flex flex-wrap gap-2">
                {product.tasting_notes.map((n) => (
                  <span key={n} className="border border-[#1A1A1A] px-3 py-1 text-xs uppercase tracking-overline">{n}</span>
                ))}
              </div>
            </div>
          )}

          {product.certifications?.length > 0 && (
            <div className="mt-6">
              <p className="text-xs uppercase tracking-overline text-[#7A7A7A] mb-3">Certifications</p>
              <div className="flex flex-wrap gap-2">
                {product.certifications.map((c) => (
                  <span key={c} className="bg-[#F5F2EA] text-[#4A5D23] px-3 py-1 text-xs uppercase tracking-overline border border-[#D5D1C5]">{c}</span>
                ))}
              </div>
            </div>
          )}

          {/* Quantity + Add */}
          <div className="mt-10 border-t border-[#D5D1C5] pt-8 flex items-center gap-4">
            <div data-testid="qty-stepper" className="flex items-center border border-[#1A1A1A]">
              <button data-testid="qty-decrease" onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:bg-[#1A1A1A] hover:text-[#FAF9F6] transition-colors"><Minus size={14}/></button>
              <span data-testid="qty-value" className="px-5 font-medium tabular-nums">{qty}</span>
              <button data-testid="qty-increase" onClick={() => setQty(qty + 1)} className="p-3 hover:bg-[#1A1A1A] hover:text-[#FAF9F6] transition-colors"><Plus size={14}/></button>
            </div>

            <button
              data-testid="add-to-cart-btn"
              onClick={onAdd}
              className="flex-1 bg-[#C84B31] text-[#FAF9F6] px-6 py-4 hover:bg-[#A93F29] transition-colors uppercase tracking-overline text-sm flex items-center justify-center gap-2"
            >
              {added ? (<><Check size={16}/> Added</>) : "Add to Cart"}
            </button>
          </div>

          <p className="mt-6 text-xs text-[#7A7A7A]">
            Flat $15 international shipping. Bulk &gt; 5 kg? <Link to="/wholesale" className="text-[#C84B31] underline underline-offset-4">Get a wholesale quote →</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
